'use client';

import { useEffect } from 'react';

/** Forms posting to the BFF (sign-in steps, tenant switch, logout, security changes). */
const AUTH_FORM = 'form[method="post" i][action^="/auth/"]';
/** A form that got no answer (network error) can be submitted again after this. */
const UNLOCK_MS = 15_000;

function reset(form: HTMLFormElement): void {
  delete form.dataset['submitting'];
  form.removeAttribute('aria-busy');
  for (const button of form.querySelectorAll<HTMLButtonElement>('button[data-submit-once]')) {
    button.disabled = false;
    delete button.dataset['submitOnce'];
  }
}

/**
 * One submission per BFF form (a double click on "Entrar en…" posted the tenant
 * switch two or three times; the extra posts raced with the session rotation).
 * Progressive enhancement, CSP-safe (bundled script, no inline handlers):
 * without JavaScript the forms work as before and the BFF itself makes a
 * duplicate harmless (docs/threat-models/auth.md §3.14). Renders nothing.
 */
export function SubmitOnce() {
  useEffect(() => {
    const timers = new Set<ReturnType<typeof setTimeout>>();
    function onSubmit(event: SubmitEvent) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches(AUTH_FORM)) {
        return;
      }
      if (form.dataset['submitting'] === '1') {
        event.preventDefault();
        return;
      }
      form.dataset['submitting'] = '1';
      form.setAttribute('aria-busy', 'true');
      // Disabled after this tick: the clicked button's name/value is still sent.
      const disable = setTimeout(() => {
        for (const button of form.querySelectorAll<HTMLButtonElement>('button')) {
          if (!button.disabled) {
            button.disabled = true;
            button.dataset['submitOnce'] = '1';
          }
        }
      }, 0);
      const unlock = setTimeout(() => {
        reset(form);
      }, UNLOCK_MS);
      timers.add(disable).add(unlock);
    }
    // Back/forward cache: a restored page must be usable again.
    function onPageShow(event: PageTransitionEvent) {
      if (event.persisted) {
        document.querySelectorAll<HTMLFormElement>(AUTH_FORM).forEach(reset);
      }
    }
    document.addEventListener('submit', onSubmit);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('submit', onSubmit);
      window.removeEventListener('pageshow', onPageShow);
      timers.forEach(clearTimeout);
    };
  }, []);
  return null;
}
